import { z } from "zod";

/**
 * Coffre documentaire (devis, module « Coffre documentaire »). Types de pièces
 * par défaut — la cartographie fixera la liste réelle et celles qui sont
 * attendues pour chaque type de dossier.
 */
export const DEFAULT_DOCUMENT_TYPES = [
  { code: "cv", label: "Curriculum vitae", requiredDefault: true, expires: false, sort: 10 },
  { code: "passeport", label: "Passeport", requiredDefault: true, expires: true, sort: 20 },
  { code: "acte_naissance", label: "Acte de naissance", requiredDefault: true, expires: false, sort: 30 },
  { code: "diplome", label: "Diplôme", requiredDefault: true, expires: false, sort: 40 },
  { code: "releve_notes", label: "Relevé de notes", requiredDefault: false, expires: false, sort: 50 },
  { code: "attestation_emploi", label: "Attestation d'emploi", requiredDefault: false, expires: false, sort: 60 },
  { code: "photo", label: "Photo d'identité", requiredDefault: false, expires: false, sort: 70 },
  { code: "formulaire", label: "Formulaire", requiredDefault: false, expires: false, sort: 80 },
  { code: "autre", label: "Autre pièce", requiredDefault: false, expires: false, sort: 90 },
] as const;

export const DOCUMENT_STATUSES = ["recue", "validee", "rejetee"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];
export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  recue: "Reçue",
  validee: "Validée",
  rejetee: "Rejetée",
};

export const CHECKLIST_STATUSES = ["manquante", "recue", "validee"] as const;
export type ChecklistStatus = (typeof CHECKLIST_STATUSES)[number];
export const CHECKLIST_STATUS_LABELS: Record<ChecklistStatus, string> = {
  manquante: "Manquante",
  recue: "Reçue",
  validee: "Validée",
};

/** Formats acceptés au dépôt : PDF, Word, images (numérisations, photos de documents papier). */
export const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/tiff",
] as const;

export const documentUpdateSchema = z.object({
  typeCode: z.string().trim().min(1).max(40).optional(),
  status: z.enum(DOCUMENT_STATUSES).optional(),
  /** AAAA-MM-JJ (passeport, permis). */
  expiresAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
});
export type DocumentUpdate = z.infer<typeof documentUpdateSchema>;

export const checklistUpdateSchema = z.object({
  /** Types de pièces attendues pour ce candidat (remplace la liste). */
  requiredTypes: z.array(z.string().trim().min(1).max(40)).max(50),
});

export interface DocumentType {
  code: string;
  label: string;
  requiredDefault: boolean;
  expires: boolean;
  sort: number;
}

export interface CandidateDocument {
  id: string;
  candidateId: string;
  typeCode: string;
  originalName: string;
  mime: string;
  size: number;
  sha256: string;
  status: DocumentStatus;
  expiresAt: string | null;
  uploadedBy: string | null;
  uploadedByName: string | null;
  uploadedAt: number | string;
  /** Lecture du texte : en attente, faite, impossible (image illisible…). */
  textStatus: "en_attente" | "fait" | "echec" | "sans_objet";
  pages: number | null;
}

export interface ChecklistItem {
  typeCode: string;
  label: string;
  status: ChecklistStatus;
  documentId: string | null;
  expiresAt: string | null;
}
